import * as React from 'react';
import Box from '@mui/material/Box';
import {
  DataGrid,
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams
} from '@mui/x-data-grid';
import { Theme, Tooltip, useTheme } from '@mui/material';
import {
  BaseImportMetadata,
  CompletedImport,
  CompletedImportMetadata,
  FailedImport,
  FailedImportMetadata,
  PendingImport
} from '@esg/esg-global-types';
import { ReactComponent as ImportSuccessIcon } from '../../../assets/icons/Correct.svg';
import { ReactComponent as ImportFailedIcon } from '../../../assets/icons/Incorrect.svg';
import DownloadingIcon from '@mui/icons-material/Downloading';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { GroupContext } from '../../../context/GroupContext';
import { CompanyContext } from '../../../context/CompanyContext';
import { UserContext } from '../../../context/UserContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { MetadataError } from '@ep/error-handling';
import { Query, QueryDocumentSnapshot, collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../../lib/google/firebase';
import moment from 'moment';
import { ViewAccess, getAdminGroups, getViewAccess } from '../../../util/user_access';
import { ImportType, import_types } from '../ImportTypeSelect';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { ReactComponent as ErrorsIcon } from '../../../assets/icons/Error.svg';
import UndoImportConfirmationModal from './UndoImportConfirmationModal';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import {
  BaseImportMetadataModalData,
  CompletedImportMetadataModalData,
  FailedImportMetadataModalData,
  import_label
} from '../../../lib/metric_capture/import';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import ImportErrorsModal from './ImportErrorsModal';
import { timestampISOString } from '../../../util/date';

type GridRowImportMetadata =
  | (Omit<BaseImportMetadata, 'import_type'> & { import_type: ImportType | string })
  | (Omit<CompletedImportMetadata, 'import_type'> & { import_type: ImportType | string })
  | (Omit<FailedImportMetadata, 'import_type'> & { import_type: ImportType | string });

export interface ImportDataGridRow {
  id: string;
  metadata: GridRowImportMetadata;
  message: string;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface ImportDataGridProps {
  parent_rows?: Array<ImportDataGridRow>;
  grid_loading?: boolean;
}

/**
 * A datagrid widget for displaying all pending, failed and completed generated data imports.
 * @param {Array<ImportDataGridRow>} parent_rows Optionally provide data imports from parent, auto fetches all imports from firestore if not provided.
 * @param {boolean | undefined} grid_loading Optionally set data grid to loading state from parent.
 * @returns {JSX.Element}
 */
const ImportDataGrid = ({ parent_rows, grid_loading }: ImportDataGridProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const { group } = React.useContext(GroupContext);
  const { company } = React.useContext(CompanyContext);
  const { user } = React.useContext(UserContext);
  const [pendingDataImports, setPendingDataImports] =
    React.useState<Array<ImportDataGridRow> | null>(null);
  const [failedDataImports, setFailedDataImports] = React.useState<Array<ImportDataGridRow> | null>(
    null
  );
  const [completedDataImports, setCompletedDataImports] =
    React.useState<Array<ImportDataGridRow> | null>(null);
  const [actionRow, setActionRow] = React.useState<ImportDataGridRow | null>(null);
  const [displayUndoModal, setDisplayUndoModal] = React.useState<boolean>(false);
  const [displayErrorsModal, setDisplayErrorsModal] = React.useState<boolean>(false);
  const theme: Theme = useTheme();

  const view_access: ViewAccess =
    user && group && company
      ? getViewAccess(user, group.id, company.id, 'sustainability.data_import')
      : null;
  const view_only: boolean = view_access === 'read';

  // Combine all types of imports into grid rows.
  const data_import_rows: Array<ImportDataGridRow> | null =
    pendingDataImports && failedDataImports && completedDataImports
      ? [...pendingDataImports, ...completedDataImports, ...failedDataImports]
      : null;

  const user_is_group_admin: boolean = !!user && !!group && getAdminGroups(user).includes(group.id);

  // Filter import rows based on current user and role.
  const getAllowedImportRows = (total_rows: Array<ImportDataGridRow>): Array<ImportDataGridRow> => {
    return total_rows.filter(
      (row: ImportDataGridRow) =>
        user &&
        (user.super_admin ||
          user_is_group_admin ||
          (row.metadata.requested_by?.id ?? '') === user.id)
    );
  };

  // Only show rows that current user is allowed to see.
  const allowed_rows: Array<ImportDataGridRow> = getAllowedImportRows(
    parent_rows ?? (data_import_rows ? data_import_rows : [])
  );

  // Display the undo confirmation modal for a clicked import.
  const handleUndoClick = (import_row: ImportDataGridRow): void => {
    setActionRow(import_row);
    setDisplayUndoModal(true);
  };

  // Display the errors breakdown modal for a clicked import.
  const handleErrorsClick = (import_row: ImportDataGridRow): void => {
    setActionRow(import_row);
    setDisplayErrorsModal(true);
  };

  // Close the undo confirmation modal for a clicked import.
  const closeUndoConfirmationModal = (): void => {
    setDisplayUndoModal(false);
  };

  // Close the errors breakdown modal for a clicked import.
  const closeErrorsModal = (): void => {
    setDisplayErrorsModal(false);
  };

  // Undo the import row dynamically using the function from the row's import type.
  const undoRow = async (import_row: ImportDataGridRow): Promise<void> => {
    try {
      if (
        group &&
        company &&
        typeof import_row.metadata.import_type !== 'string' &&
        import_row.metadata.import_type.handleUndo
      ) {
        await import_row.metadata.import_type.handleUndo(
          group.id,
          company.id,
          import_row.metadata.tracking_id
        );
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ImportDataGrid failed on an unknown error while calling undoRow.',
          {
            import_row: import_row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to undo Data Import. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  // Get all pending, completed and failed imports for current company and create collection listeners if rows not provided by parent, attach full import type object.
  React.useEffect(() => {
    try {
      if (group && company && user && parent_rows === undefined) {
        const collection_prefix = `groups/${group.id}/companies/${company.id}`;
        const completed_imports_query: Query = query(
          collection(db, `${collection_prefix}/completed_imports`)
        );
        const failed_imports_query: Query = query(
          collection(db, `${collection_prefix}/failed_imports`)
        );
        const pending_imports_query: Query = query(
          collection(db, `${collection_prefix}/pending_imports`)
        );
        onSnapshot(completed_imports_query, (querySnapshot) => {
          const completed_imports: Array<CompletedImport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              const completed_metadata = doc.data().metadata as CompletedImportMetadataModalData;
              completed_imports.push({
                id: doc.id,
                metadata: {
                  ...completed_metadata,
                  started: timestampISOString(completed_metadata.started),
                  completed: timestampISOString(completed_metadata.completed)
                },
                message: doc.data().message
              });
            }
          });
          setCompletedDataImports(
            completed_imports.map((completed_import: CompletedImport) => {
              return {
                id: completed_import.id,
                metadata: {
                  ...completed_import.metadata,
                  import_type:
                    import_types.find(
                      (import_type: ImportType) =>
                        import_type.id === completed_import.metadata.import_type
                    ) ?? completed_import.metadata.import_type
                },
                message: completed_import.message,
                status: 'Completed'
              };
            })
          );
        });
        onSnapshot(failed_imports_query, (querySnapshot) => {
          const failed_imports: Array<FailedImport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              const failed_metadata = doc.data().metadata as FailedImportMetadataModalData;
              failed_imports.push({
                id: doc.id,
                metadata: {
                  ...failed_metadata,
                  started: timestampISOString(failed_metadata.started),
                  failed: timestampISOString(failed_metadata.failed)
                },
                message: doc.data().message
              });
            }
          });
          setFailedDataImports(
            failed_imports.map((failed_import: FailedImport) => {
              return {
                id: failed_import.id,
                metadata: {
                  ...failed_import.metadata,
                  import_type:
                    import_types.find(
                      (import_type: ImportType) =>
                        import_type.id === failed_import.metadata.import_type
                    ) ?? failed_import.metadata.import_type
                },
                message: failed_import.message,
                status: 'Failed'
              };
            })
          );
        });
        onSnapshot(pending_imports_query, (querySnapshot) => {
          const pending_imports: Array<PendingImport> = [];
          querySnapshot.forEach((doc: QueryDocumentSnapshot) => {
            if (doc.data().metadata) {
              const pending_metadata = doc.data().metadata as BaseImportMetadataModalData;
              pending_imports.push({
                id: doc.id,
                metadata: {
                  ...pending_metadata,
                  started: timestampISOString(pending_metadata.started)
                },
                message: doc.data().message
              });
            }
          });
          setPendingDataImports(
            pending_imports.map((pending_import: PendingImport) => {
              return {
                id: pending_import.id,
                metadata: {
                  ...pending_import.metadata,
                  import_type:
                    import_types.find(
                      (import_type: ImportType) =>
                        import_type.id === pending_import.metadata.import_type
                    ) ?? pending_import.metadata.import_type
                },
                message: pending_import.message,
                status: 'Pending'
              };
            })
          );
        });
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : 'Error: ImportDataGrid failed on an unknown error while initialising import document listeners.',
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch imports from database. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  }, [group, company]);

  const columns: Array<GridColDef> = [
    {
      field: 'status',
      maxWidth: 40,
      headerName: '',
      renderCell: (params: GridRenderCellParams) => {
        return (
          (params.row.status === 'Pending' && (
            <>
              <Tooltip title="Started" key={1}>
                <DownloadingIcon color="info" />
              </Tooltip>
            </>
          )) ||
          (params.row.status === 'Failed' && (
            <>
              <Tooltip title="Failed" key={2}>
                <ImportFailedIcon
                  height={30}
                  stroke={theme.palette.error.main}
                  fill="none"
                  strokeWidth={5}
                />
              </Tooltip>
            </>
          )) ||
          (params.row.status === 'Completed' && (
            <>
              <Tooltip title="Completed" key={3}>
                <ImportSuccessIcon
                  height={30}
                  stroke={theme.palette.success.main}
                  fill="none"
                  strokeWidth={5}
                />
              </Tooltip>
            </>
          ))
        );
      },
      flex: 0.5
    },
    {
      field: 'import_type',
      headerName: 'Import Type',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metadata.import_type
          ? typeof params.row.metadata.import_type === 'string'
            ? params.row.metadata.import_type.replaceAll('_', ' ')
            : params.row.metadata.import_type.name
          : '';
      },
      flex: 1
    },
    {
      field: 'source',
      headerName: 'Source',
      valueGetter: (params: GridValueGetterParams) => {
        return params.row.metadata.source ? params.row.metadata.source : 'N/A';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
      flex: 1
    },
    {
      field: 'started',
      headerName: 'Started',
      valueGetter: (params: GridValueGetterParams) =>
        moment(params.row.metadata.started).format('YYYY-MM-DD HH:mm'),
      flex: 1
    },
    {
      field: 'finished',
      headerName: 'Finished',
      valueGetter: (params: GridValueGetterParams) =>
        params.row.metadata.completed
          ? moment(params.row.metadata.completed).format('YYYY-MM-DD HH:mm')
          : params.row.metadata.failed
            ? moment(params.row.metadata.failed).format('YYYY-MM-DD HH:mm')
            : '',
      flex: 1
    },
    {
      field: 'message',
      headerName: 'Message',
      sortable: false,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.row.status === 'Failed'
              ? `${params.value} - View details with the errors breakdown button to the right`
              : params.value}
          </span>
        </Tooltip>
      ),
      flex: 3
    },
    {
      field: 'actions',
      type: 'actions',
      align: 'center',
      flex: 1,
      getActions: ({ row }) => {
        const import_type_actions: Array<React.ReactElement<GridActionsCellItemProps>> = [];
        if (row.status === 'Completed' && row.metadata.import_type?.handleUndo && !view_only) {
          import_type_actions.push(
            <>
              <Tooltip title="Undo Import" key={1}>
                <span>
                  <GridActionsCellItem
                    icon={<DeleteIcon width={25} fill={theme.palette.primary.main} />}
                    label="Undo Import"
                    onClick={() => handleUndoClick(row)}
                  />
                </span>
              </Tooltip>
            </>
          );
        }
        if (row.status === 'Failed') {
          import_type_actions.push(
            <>
              <Tooltip title="View Import Errors" key={1}>
                <span>
                  <GridActionsCellItem
                    icon={
                      <ErrorsIcon
                        width={25}
                        fill={theme.palette.primary.main}
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                      />
                    }
                    label="Import Errors"
                    onClick={() => handleErrorsClick(row)}
                  />
                </span>
              </Tooltip>
            </>
          );
        }
        return import_type_actions;
      }
    }
  ];

  return (
    <>
      {/* Undo Confirmation Modal */}
      <UndoImportConfirmationModal
        open={displayUndoModal}
        handleCloseUndoModal={closeUndoConfirmationModal}
        import_label={
          actionRow && typeof actionRow.metadata.import_type !== 'string'
            ? actionRow.metadata.import_type.name
            : actionRow?.metadata.tracking_id
        }
        import_created={actionRow && actionRow.metadata.started}
        handleUndoImport={
          actionRow &&
          (async () => {
            await undoRow(actionRow);
          })
        }
      />
      {/* Errors breakdown modal */}
      <ImportErrorsModal
        open={displayErrorsModal}
        handleCloseErrorsModal={closeErrorsModal}
        import_row={actionRow}
      />
      <Box>
        <DataGrid
          loading={grid_loading ?? (!parent_rows && data_import_rows === null)}
          autoHeight
          rows={allowed_rows}
          columns={columns}
          disableColumnMenu
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5
              }
            },
            sorting: {
              sortModel: [{ field: 'started', sort: 'desc' }]
            },
            columns: {
              columnVisibilityModel: {
                completed: false
              }
            }
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
          slots={{
            toolbar: () => <DataGridToolbar entity_label={import_label} />,
            noRowsOverlay: () => <DataGridNoRows missing_label={import_label} />
          }}
          sx={{
            '&, [class^=MuiDataGrid]': { border: 'none' }
          }}
        />
      </Box>
    </>
  );
};

export default ImportDataGrid;
